import axios from 'axios';
import client from './client';
import { API_URL, USE_MOCKS } from './config';
import { normalizeLogin } from '../utils/login';
import { normalizeReferralCode } from '../utils/referral';

const SITE_STATUS_TIMEOUT_MS = 5000;

export async function getSiteStatus(signal) {
    if (USE_MOCKS) {
        if (signal?.aborted) {
            const err = new Error('canceled');
            err.code = 'ERR_CANCELED';
            throw err;
        }
        const siteStatusMock = await import('./mocks/siteStatus');
        return siteStatusMock.mockGetSiteStatus();
    }

    const { data } = await axios.get(`${API_URL}/site/status`, {
        signal,
        timeout: SITE_STATUS_TIMEOUT_MS,
        headers: { Accept: 'application/json' },
    });

    return data;
}

export async function login(login, password) {
    if (USE_MOCKS) {
        const authMock = await import('./mocks/auth');
        return authMock.mockLogin(login, password);
    }
    const { data } = await client.post('/auth/login', {
        login: normalizeLogin(login),
        password,
    });
    return data;
}

export async function register(login, password) {
    if (USE_MOCKS) {
        const authMock = await import('./mocks/auth');
        return authMock.mockRegister(login, password);
    }
    const { data } = await client.post('/auth/register', {
        login: normalizeLogin(login),
        password,
    });
    return data;
}

export async function logout() {
    if (USE_MOCKS) {
        const authMock = await import('./mocks/auth');
        return authMock.mockLogout();
    }
    const { data } = await client.post('/auth/logout');
    return data;
}

export async function changePassword(oldPassword, newPassword) {
    if (USE_MOCKS) {
        const authMock = await import('./mocks/auth');
        return authMock.mockChangePassword(oldPassword, newPassword);
    }
    const { data } = await client.post('/auth/changePassword', {
        old_password: oldPassword,
        new_password: newPassword,
    });
    return data;
}

export async function getMySubscriptions() {
    if (USE_MOCKS) {
        const subscriptionsMock = await import('./mocks/subscriptions');
        return subscriptionsMock.mockGetMySubscriptions();
    }
    const { data } = await client.get('/subscriptions/my');
    return data;
}

export async function getProfile() {
    if (USE_MOCKS) {
        const accountMock = await import('./mocks/account');
        return accountMock.mockGetProfile();
    }
    const { data } = await client.get('/account/profile');
    return data;
}

export async function bindReferralCode(code) {
    if (USE_MOCKS) {
        const referralMock = await import('./mocks/referral');
        return referralMock.mockBindReferralCode(code);
    }
    const { data } = await client.post('/account/bindReferralCode', {
        referral_code: normalizeReferralCode(code),
    });
    return data;
}

export async function deleteSubscription(subscriptionId) {
    if (USE_MOCKS) {
        const subscriptionsMock = await import('./mocks/subscriptions');
        return subscriptionsMock.mockDeleteSubscription(subscriptionId);
    }
    const { data } = await client.delete(`/subscriptions/${subscriptionId}`);
    return data;
}

export async function getSubscriptionConfig(signal) {
    if (USE_MOCKS) {
        if (signal?.aborted) {
            const err = new Error('canceled');
            err.code = 'ERR_CANCELED';
            throw err;
        }
        const configMock = await import('./mocks/subscriptionConfig');
        return configMock.mockGetSubscriptionConfig();
    }
    const { data } = await client.get('/subscription/config', { signal });
    return data;
}

export async function getSubscriptionPricing(signal) {
    if (USE_MOCKS) {
        if (signal?.aborted) {
            const err = new Error('canceled');
            err.code = 'ERR_CANCELED';
            throw err;
        }
        const pricingMock = await import('./mocks/pricing');
        return pricingMock.mockGetSubscriptionPricing();
    }
    const { data } = await client.get('/subscription/pricing', { signal });
    return data;
}

export async function createPaymentLink(options = {}) {
    const pointsToSpend = Math.max(0, Math.floor(options.pointsToSpend || 0));
    const promoCode = options.promoCode?.trim() || '';
    const devices = options.devices;

    if (devices == null) {
        throw new Error('Не указано количество устройств');
    }

    if (USE_MOCKS) {
        const paymentsMock = await import('./mocks/payments');
        return paymentsMock.mockCreatePaymentLink(
            pointsToSpend,
            devices,
            promoCode,
        );
    }
    const body = {
        devices,
        points_to_spend: pointsToSpend,
    };
    if (promoCode) {
        body.promo_code = promoCode;
    }
    const { data } = await client.post('/payments/createPaymentLink', body);
    return data;
}

export async function activateSubscriptionFromPayment(options = {}) {
    const devices = options.devices;
    const pointsToSpend = Math.max(0, Math.floor(options.pointsToSpend || 0));
    const cashPaid = options.cashPaid;
    const paymentId = options.paymentId;

    if (devices == null) {
        throw new Error('Не указано количество устройств');
    }

    if (USE_MOCKS) {
        const paymentsMock = await import('./mocks/payments');
        return paymentsMock.mockActivateSubscriptionFromPayment(
            devices,
            { pointsToSpend, cashPaid, paymentId },
        );
    }
    const body = {
        devices,
        points_to_spend: pointsToSpend,
    };
    if (paymentId != null) {
        body.payment_id = paymentId;
    }
    if (cashPaid != null) {
        body.cash_paid = cashPaid;
    }
    const { data } = await client.post('/payments/confirm', body);
    return data;
}

export async function validatePromoCode(code) {
    if (USE_MOCKS) {
        const promoMock = await import('./mocks/promo');
        return promoMock.mockValidatePromoCode(code);
    }
    const { data } = await client.post('/promo/validate', {
        promo_code: code.trim(),
    });
    return data;
}

export async function recordMetric({ tag, user, visitor_id, metadata }) {
    try {
        if (USE_MOCKS) {
            const metricsMock = await import('./mocks/metrics');
            return metricsMock.mockRecordMetric({ tag, user, visitor_id, metadata });
        }

        const body = { tag };

        if (user) {
            body.user = user;
        }

        if (visitor_id) {
            body.visitor_id = visitor_id;
        }

        if (metadata && Object.keys(metadata).length > 0) {
            body.metadata = metadata;
        }

        const { data } = await axios.post(`${API_URL}/metrics/record`, body, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
            validateStatus: () => true,
        });

        return data;
    } catch {
        return null;
    }
}
